import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './Register.css';

/**
 * 註冊頁：輸入姓名 + 電話 → 送 Telegram 給管理者審核 → 核准後寫入 D1 名單
 * onRegistered(name) 於審核通過時呼叫
 */
export default function Register({ onRegistered }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [regId, setRegId] = useState(() => {
    try { return localStorage.getItem('pendingRegId') || ''; } catch { return ''; }
  });
  const [regStatus, setRegStatus] = useState(regId ? 'pending' : '');
  const pollRef = useRef(null);

  // 待審核時每 5 秒輪詢一次結果
  useEffect(() => {
    if (!regId || regStatus !== 'pending') return undefined;

    const poll = async () => {
      try {
        const res = await api.getRegistration(regId);
        if (res.success && res.data) {
          if (res.data.status === 'approved') {
            setRegStatus('approved');
            try { localStorage.removeItem('pendingRegId'); } catch {}
            try {
              onRegistered(res.data.name);
            } catch (e) {
              setError('登入失敗，請重新整理頁面或聯絡管理員');
            }
          } else if (res.data.status === 'rejected') {
            setRegStatus('rejected');
            try { localStorage.removeItem('pendingRegId'); } catch {}
          }
        }
      } catch (e) {
        // 忽略暫時性網路錯誤，下一輪再試
      }
    };

    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [regId, regStatus, onRegistered]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedPhone = phone.replace(/[\s-]/g, '').trim();

    if (trimmedName.length < 2) { setError('請輸入正確姓名'); return; }
    if (!/^[0-9+]{6,15}$/.test(trimmedPhone)) { setError('請輸入正確電話號碼'); return; }

    setSubmitting(true);
    try {
      const res = await api.register({ name: trimmedName, phone: trimmedPhone });
      if (!res.success) {
        setError(res.message || '送出失敗，請重試');
      } else if (res.status === 'approved') {
        onRegistered(res.name || trimmedName);
      } else {
        setRegId(res.id);
        setRegStatus('pending');
        try { localStorage.setItem('pendingRegId', res.id); } catch {}
      }
    } catch (err) {
      setError('網路錯誤，請重試');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setRegId('');
    setRegStatus('');
    setError('');
    try { localStorage.removeItem('pendingRegId'); } catch {}
  };

  // 等待審核中
  if (regStatus === 'pending') {
    return (
      <div className="app-container">
        <div className="register-card">
          <h1>🏢 NFC 打卡系統</h1>
          <div className="reg-waiting">
            <div className="reg-spinner" />
            <h2>等待管理者審核</h2>
            <p>您的註冊申請已送出，管理者核准後即可開始打卡。</p>
            <p className="reg-hint">此頁面會自動更新，請保持開啟。</p>
          </div>
          <button className="btn-link" onClick={handleReset}>重新填寫</button>
        </div>
      </div>
    );
  }

  // 被拒絕
  if (regStatus === 'rejected') {
    return (
      <div className="app-container">
        <div className="register-card">
          <h1>🏢 NFC 打卡系統</h1>
          <div className="reg-rejected">
            <div className="reg-icon">❌</div>
            <h2>申請未通過</h2>
            <p>您的註冊申請已被管理者拒絕，如有疑問請聯絡主管。</p>
          </div>
          <button className="btn-link" onClick={handleReset}>重新申請</button>
        </div>
      </div>
    );
  }

  // 註冊表單
  return (
    <div className="app-container">
      <div className="register-card">
        <h1>🏢 NFC 打卡系統</h1>
        <p className="register-subtitle">新員工註冊</p>

        <form onSubmit={handleSubmit} className="register-form">
          <label>
            <span>姓名</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="請輸入姓名"
              autoComplete="name"
              disabled={submitting}
            />
          </label>

          <label>
            <span>電話</span>
            <input
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="例：88888888"
              autoComplete="tel"
              disabled={submitting}
            />
          </label>

          {error && <div className="register-error">{error}</div>}

          <button type="submit" className="btn-register" disabled={submitting}>
            {submitting ? '送出中...' : '送出註冊申請'}
          </button>
        </form>

        <p className="register-note">
          送出後將通知管理者審核，核准後即可使用 NFC 打卡。
        </p>
      </div>
    </div>
  );
}
